/**
 * TileBuildingModal.jsx - Full UI modal for selecting and building on tiles
 * Replaces the compact dropdown with a full-screen modal for better late-game UX
 */

import React, { useState, useMemo } from 'react';
import { useGame } from '../../context/GameContext';
import { buildings as BUILDINGS } from '../../data/buildings';
import { resources } from '../../data/resources';
import { BIOMES } from '../../data/world-map/biomes';
import BuildingChip from '../common/BuildingChip';
import ResourceChip from '../common/ResourceChip';
import { canAfford } from '../../modules/shared';
import './TileBuildingModal.css';

// Get all unique tiers from available buildings
const getUniqueTiers = (buildings) => {
  const tiers = [...new Set(buildings.map(b => b.tier))].sort((a, b) => a - b);
  return tiers;
};

// Get all unique categories from available buildings
const getUniqueCategories = (buildings) => {
  const categories = [...new Set(buildings.map(b => b.category || 'Other'))].sort();
  return categories;
};

export function TileBuildingModal({ onClose, onBuild }) {
  const {
    worldMap,
    playerResources,
    buildingCounts,
    getTileBuildCost,
    researchedTechs,
  } = useGame();

  // Check if a building's tech requirement is met
  const isTechUnlocked = (building) => {
    const requiredTech = building.unlockCondition?.tech;
    if (!requiredTech) return true; // No tech requirement
    return researchedTechs?.includes(requiredTech);
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTier, setSelectedTier] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('tier'); // tier, name, cost
  const [selectedBuilding, setSelectedBuilding] = useState(null);

  // Get current node data
  const currentNodeId = worldMap.currentNode;
  const currentNode = worldMap.discoveredNodes?.[currentNodeId];
  const biome = currentNode?.biome ? BIOMES[currentNode.biome] : null;

  // Get available buildings for this tile
  const availableBuildings = useMemo(() => {
    if (!biome?.associatedBuildings) return [];
    return biome.associatedBuildings
      .map(id => BUILDINGS[id])
      .filter(b => b); // Filter out undefined
  }, [biome]);

  // Filter and sort buildings
  const filteredBuildings = useMemo(() => {
    let result = [...availableBuildings];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(b =>
        b.name.toLowerCase().includes(query) ||
        b.description?.toLowerCase().includes(query) ||
        Object.keys(b.baseProduction || {}).some(r => r.toLowerCase().includes(query))
      );
    }

    // Filter by tier
    if (selectedTier !== 'all') {
      result = result.filter(b => b.tier === parseInt(selectedTier));
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      result = result.filter(b => (b.category || 'Other') === selectedCategory);
    }

    // Sort
    switch (sortBy) {
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'cost':
        result.sort((a, b) => {
          const costA = Object.values(getTileBuildCost?.(a.id) || {}).reduce((s, v) => s + v, 0);
          const costB = Object.values(getTileBuildCost?.(b.id) || {}).reduce((s, v) => s + v, 0);
          return costA - costB;
        });
        break;
      case 'tier':
      default:
        result.sort((a, b) => a.tier - b.tier || a.name.localeCompare(b.name));
        break;
    }

    return result;
  }, [availableBuildings, searchQuery, selectedTier, selectedCategory, sortBy, getTileBuildCost]);

  // Get unique tiers and categories for filters
  const uniqueTiers = useMemo(() => getUniqueTiers(availableBuildings), [availableBuildings]);
  const uniqueCategories = useMemo(() => getUniqueCategories(availableBuildings), [availableBuildings]);

  // Handle building selection
  const handleSelectBuilding = (building) => {
    setSelectedBuilding(selectedBuilding?.id === building.id ? null : building);
  };

  // Handle build action
  const handleBuild = () => {
    if (!selectedBuilding) return;
    const cost = getTileBuildCost?.(selectedBuilding.id);
    if (!canAfford(cost, playerResources)) return;

    onBuild(selectedBuilding.id);
    onClose();
  };

  // Handle double-click to quick build
  const handleDoubleClick = (building) => {
    const cost = getTileBuildCost?.(building.id);
    if (!canAfford(cost, playerResources)) return;

    onBuild(building.id);
    onClose();
  };

  // Get selected building details
  const selectedBuildingData = selectedBuilding ? BUILDINGS[selectedBuilding.id] : null;
  const selectedCost = selectedBuilding ? getTileBuildCost?.(selectedBuilding.id) : null;
  const canAffordSelected = selectedCost ? canAfford(selectedCost, playerResources) : false;
  const selectedUnlocked = selectedBuilding ? isTechUnlocked(selectedBuilding) : true;
  const selectedRequiredTech = selectedBuilding?.unlockCondition?.tech;

  return (
    <div className="tbm-overlay" onClick={onClose}>
      <div className="tbm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="tbm-header">
          <div className="tbm-header-left">
            <span className="tbm-icon">🏗️</span>
            <h2>Build on Tile</h2>
            <span className="tbm-cost-badge">2× Cost</span>
          </div>
          <div className="tbm-header-right">
            <div className="tbm-location">
              <span className="tbm-biome-icon">{biome?.icon || '🗺️'}</span>
              <span className="tbm-biome-name">{biome?.name || 'Unknown'}</span>
            </div>
            <button className="tbm-close" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="tbm-filters">
          <div className="tbm-search">
            <input
              type="text"
              placeholder="Search buildings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="tbm-search-input"
            />
          </div>
          <div className="tbm-filter-group">
            <label>Tier:</label>
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              className="tbm-select"
            >
              <option value="all">All</option>
              {uniqueTiers.map(tier => (
                <option key={tier} value={tier}>T{tier}</option>
              ))}
            </select>
          </div>
          <div className="tbm-filter-group">
            <label>Sort:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="tbm-select"
            >
              <option value="tier">By Tier</option>
              <option value="name">By Name</option>
              <option value="cost">By Cost</option>
            </select>
          </div>
        </div>

        {/* Main Content */}
        <div className="tbm-content">
          {/* Building List */}
          <div className="tbm-building-list">
            {filteredBuildings.length === 0 ? (
              <div className="tbm-empty">
                {searchQuery ? 'No buildings match your search' : 'No buildings available'}
              </div>
            ) : (
              filteredBuildings.map(building => {
                const cost = getTileBuildCost?.(building.id) || {};
                const affordable = canAfford(cost, playerResources);
                const owned = buildingCounts?.[building.id] || 0;
                const isSelected = selectedBuilding?.id === building.id;
                const unlocked = isTechUnlocked(building);
                const requiredTech = building.unlockCondition?.tech;

                return (
                  <div
                    key={building.id}
                    className={`tbm-building-card ${isSelected ? 'selected' : ''} ${!affordable ? 'unaffordable' : ''} ${!unlocked ? 'locked' : ''}`}
                    onClick={() => handleSelectBuilding(building)}
                    onDoubleClick={() => unlocked && handleDoubleClick(building)}
                  >
                    <div className="tbm-building-header">
                      <span className="tbm-building-icon">{building.icon}</span>
                      <div className="tbm-building-info">
                        <span className="tbm-building-name">{building.name}</span>
                        <span className="tbm-building-tier">Tier {building.tier}</span>
                      </div>
                      {!unlocked ? (
                        <span className="tbm-building-locked">🔒</span>
                      ) : owned > 0 ? (
                        <span className="tbm-building-owned">×{owned}</span>
                      ) : null}
                    </div>
                    {unlocked ? (
                      <div className="tbm-building-costs">
                        {Object.entries(cost).map(([resId, amount]) => {
                          const have = playerResources[resId] || 0;
                          const canAffordThis = have >= amount;
                          return (
                            <div
                              key={resId}
                              className={`tbm-cost-chip ${!canAffordThis ? 'insufficient' : ''}`}
                            >
                              <span className="tbm-cost-icon">{resources[resId]?.icon || '📦'}</span>
                              <span className="tbm-cost-amount">{amount}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="tbm-locked-message">
                        Requires: {requiredTech?.replace(/_/g, ' ')}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Selected Building Details Panel */}
          <div className={`tbm-details ${selectedBuilding ? 'has-selection' : ''}`}>
            {selectedBuildingData ? (
              <>
                <div className="tbm-details-header">
                  <span className="tbm-details-icon">{selectedBuildingData.icon}</span>
                  <div className="tbm-details-title">
                    <h3>{selectedBuildingData.name}</h3>
                    <span className="tbm-details-tier">Tier {selectedBuildingData.tier}</span>
                  </div>
                </div>

                <p className="tbm-details-description">
                  {selectedBuildingData.description}
                </p>

                {selectedBuildingData.flavor && (
                  <p className="tbm-details-flavor">"{selectedBuildingData.flavor}"</p>
                )}

                {/* Production */}
                {selectedBuildingData.baseProduction && Object.keys(selectedBuildingData.baseProduction).length > 0 && (
                  <div className="tbm-details-section">
                    <div className="tbm-details-label">Produces</div>
                    <div className="tbm-details-production">
                      {Object.entries(selectedBuildingData.baseProduction).map(([resId, amount]) => (
                        <div key={resId} className="tbm-production-item">
                          <span className="tbm-prod-icon">{resources[resId]?.icon || '📦'}</span>
                          <span className="tbm-prod-name">{resources[resId]?.name || resId}</span>
                          <span className="tbm-prod-amount">+{amount}/s</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cost Breakdown */}
                <div className="tbm-details-section">
                  <div className="tbm-details-label">Construction Cost (2×)</div>
                  <div className="tbm-details-costs">
                    {selectedCost && Object.entries(selectedCost).map(([resId, amount]) => {
                      const have = playerResources[resId] || 0;
                      const canAffordThis = have >= amount;
                      return (
                        <div
                          key={resId}
                          className={`tbm-detail-cost ${!canAffordThis ? 'insufficient' : ''}`}
                        >
                          <span className="tbm-cost-icon">{resources[resId]?.icon || '📦'}</span>
                          <span className="tbm-cost-name">{resources[resId]?.name || resId}</span>
                          <span className="tbm-cost-amount">{amount}</span>
                          <span className={`tbm-cost-have ${canAffordThis ? 'has' : 'lacks'}`}>
                            ({have})
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Tech Lock Warning */}
                {!selectedUnlocked && (
                  <div className="tbm-details-locked-warning">
                    <span className="tbm-lock-icon">🔒</span>
                    <span>Requires: <strong>{selectedRequiredTech?.replace(/_/g, ' ')}</strong></span>
                  </div>
                )}

                {/* Build Button */}
                <button
                  className={`tbm-build-btn ${(canAffordSelected && selectedUnlocked) ? '' : 'disabled'}`}
                  onClick={handleBuild}
                  disabled={!canAffordSelected || !selectedUnlocked}
                >
                  {!selectedUnlocked ? '🔒 Locked' : canAffordSelected ? '🔨 Build Here' : 'Cannot Afford'}
                </button>
              </>
            ) : (
              <div className="tbm-details-empty">
                <span className="tbm-details-empty-icon">🏗️</span>
                <p>Select a building to see details</p>
                <p className="tbm-details-empty-hint">Double-click to quick build</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Info */}
        <div className="tbm-footer">
          <div className="tbm-footer-info">
            <span className="tbm-footer-icon">💡</span>
            <span>Buildings placed on tiles cost 2× but unlock mass production in the Buildings panel (+5 capacity each)</span>
          </div>
          <div className="tbm-footer-count">
            {filteredBuildings.length} building{filteredBuildings.length !== 1 ? 's' : ''} available
          </div>
        </div>
      </div>
    </div>
  );
}

export default TileBuildingModal;
